/**
 * Recovery for a tab that outlived a deploy.
 *
 * Its bundle still asks for the OLD content-hashed chunk names, which the host
 * no longer serves (Netlify answers them with the SPA's `index.html`, which the
 * browser refuses as a module). Vite raises `vite:preloadError` for that; the
 * handler here reloads the page ONCE so it picks up the current asset graph,
 * guarded so a genuinely broken deploy cannot loop.
 *
 * The catch is what Vite does after the handler calls `preventDefault()`: it
 * stops throwing and RESOLVES the dynamic import to `undefined`. Every
 * `import('./X.js').then((m) => ({ default: m.X }))` then dies on `m.X` with
 * `Cannot read properties of undefined (reading 'X')` — a React crash reported
 * to the operator, and an error screen flashed at the user, in the moment
 * before the reload replaces the page. That is the `/dashboard`
 * `reading 'GlobalAuthModal'` report, from tabs still holding a previous
 * deploy's bundle.
 *
 * So a loader that fails while a reload is under way waits instead
 * ({@link holdWhileReloading}): Suspense keeps its fallback until the page goes
 * away. A failure with no reload pending still rejects as before.
 *
 * @module
 */

import { lazy, type LazyExoticComponent } from 'react'

/** Whatever `React.lazy` accepts as a component (its own `ComponentType<any>` bound). */
type LazyComponent = Awaited<ReturnType<Parameters<typeof lazy>[0]>>['default']

const RELOAD_GUARD = 'mol-preload-error-reloaded-at'
const RELOAD_GUARD_WINDOW_MS = 60_000

let reloadPending = false

/**
 * Whether the page is reloading to recover from a stale chunk.
 *
 * @returns `true` once {@link installChunkReloadHandler}'s listener has started a reload.
 */
export function isChunkReloadPending(): boolean {
  return reloadPending
}

/**
 * Listen for `vite:preloadError` and reload the page once per guard window.
 *
 * @param win - The window to listen on (injectable for tests).
 * @returns A function that removes the listener.
 */
export function installChunkReloadHandler(win: Window = window): () => void {
  const onPreloadError = (event: Event): void => {
    if (reloadPending) {
      event.preventDefault()
      return
    }
    let reloadedRecently = false
    try {
      const last = Number(win.sessionStorage.getItem(RELOAD_GUARD) ?? 0)
      reloadedRecently = Date.now() - last < RELOAD_GUARD_WINDOW_MS
      if (!reloadedRecently) win.sessionStorage.setItem(RELOAD_GUARD, String(Date.now()))
    } catch (_error) {
      // sessionStorage unavailable (privacy mode) — a reload is still the better
      // outcome than a blank page; the loop guard just can't persist.
    }
    // A second failure inside the window means the deploy itself is broken:
    // let Vite throw so the error boundary shows it instead of looping.
    if (reloadedRecently) return
    reloadPending = true
    event.preventDefault()
    win.location.reload()
  }
  win.addEventListener('vite:preloadError', onPreloadError)
  return () => win.removeEventListener('vite:preloadError', onPreloadError)
}

/**
 * `.catch` handler for a chunk loader: while a stale-chunk reload is under way,
 * return a promise that never settles (the page is about to be replaced);
 * otherwise rethrow.
 *
 * @param error - Whatever the loader rejected with.
 * @returns A never-settling promise while reloading.
 */
export function holdWhileReloading<T = never>(error: unknown): Promise<T> {
  if (reloadPending) return new Promise<T>(() => {})
  return Promise.reject(error)
}

/**
 * `React.lazy` whose loader waits out a stale-chunk reload instead of crashing.
 *
 * @param loader - Resolves the module with the component as `default`.
 * @returns The lazy component.
 */
export function lazyWithReload<T extends LazyComponent>(
  loader: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(() =>
    loader()
      .then((m) => {
        // A bare `() => import('./X.js')` resolves to `undefined` rather than
        // rejecting once the preload handler has called preventDefault().
        if (m == null) throw new Error('Lazy chunk resolved to no module')
        return m
      })
      .catch(holdWhileReloading<{ default: T }>),
  )
}

/** Test-only: forget a pending reload. */
export function resetChunkReloadForTests(): void {
  reloadPending = false
}
